import React, { useEffect, useState } from 'react';
import { Page } from '@components/page';
import { LeftOutlined } from '@ant-design/icons';
import { SelectItemsBody } from './components/select-items-body';
import { SelectItemsActions } from './components/select-items-actions';
import { UserBonification } from './components/user-bonification-modal';
import {
    customerSelector,
    draftsSelector,
    fetchAvailableItems,
    fetchAvailablePaymentConditions,
    fetchConvenio,
    fetchCustos,
    fetchMoedas,
    fetchMoedasSimbolos,
    fetchOrder,
    fetchPaymentCondition,
    fetchPdiscqtWithType,
    fetchSemaphores,
    orderItemsSelector,
    orderTypeSelector,
    resetBonification,
    updateMoeda,
    updateOrder,
} from '@redux/slices/order';
import { useQuery } from '@hooks/use-query';
import { useDispatch, useSelector } from 'react-redux';
import { scheduledSelector } from '@redux/slices/order/scheduled';
import { approvalMarketingSelector } from '@redux/slices/approval-marketing';
import { ApproverResponseApi } from '@models/approval/response';
import { OrderContextProvider } from './context/provider';
import { clearMinOrderState } from '@redux/slices/min-order';
import { translate } from '@components/i18n';
import { AppDispatch } from '@redux/store';
import { COUNTRY } from '@config/env';
import { COUNTRIES } from 'src/enums/countries';
import { SelectItemsHeaderMarketingBonification } from './components/select-items-header-marketing-bonification';
import { updatePaymentCondition } from '@redux/slices/order/select-items';

export const MarketingBonitication: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const query = useQuery();
    const dispatch = useDispatch<AppDispatch>();
    const { drafts } = useSelector(draftsSelector);
    const { scheduled } = useSelector(scheduledSelector);
    const { customer } = useSelector(customerSelector);
    const { orderType } = useSelector(orderTypeSelector);

    const { availableApprovalMarketing } = useSelector(
        approvalMarketingSelector,
    );

    const {
        semaphores,
        custos,
        convenios,
        pdiscqt,
        order,
        paymentCondition,
        selectedMoeda,
        moedaSimbolos,
        moedas,
        ...selectItemsState
    } = useSelector(orderItemsSelector);

    const isDraft = query.get('draft') === 'true';

    useEffect(() => {
        dispatch(fetchMoedas())
        dispatch(fetchMoedasSimbolos())
    }, [])

    const fetchPreData = async (): Promise<void> => {
        const arrayDispatch = []

        if (isDraft && moedas.length > 0 && moedaSimbolos.length > 0) {
            const moeda = moedas.find(item => item.codigo === order?.codigoMoeda)
            const simbolo = moedaSimbolos.find(item => item.codigoMoeda === order?.codigoMoeda)

            if (moeda) {
                dispatch(updateMoeda({
                    ...moeda,
                    simbolo: simbolo?.simbolo
                }))

                arrayDispatch.push(dispatch(fetchCustos({ moeda: moeda.codigo })).unwrap())
            }
        } else if ((!isDraft) && selectedMoeda?.codigo) {
            arrayDispatch.push(dispatch(fetchCustos({ moeda: selectedMoeda.codigo })).unwrap())
        }

        await Promise.all([
            dispatch(fetchConvenio()).unwrap(),
            dispatch(fetchPdiscqtWithType(order?.customer?.typeId)).unwrap(),
            dispatch(fetchSemaphores()).unwrap(),
            dispatch(fetchPaymentCondition({ data: order.customer.code })).unwrap(),
            dispatch(fetchAvailablePaymentConditions()).unwrap(),
            dispatch(resetBonification()),
            dispatch(clearMinOrderState()),
        ]).catch(error => {
            console.error(error)
        })
    }

    useEffect(() => {
        if (order?.customer?.code && moedas?.length && moedaSimbolos?.length) {
            fetchPreData()
        }
    }, [order?.customer?.code, moedas?.length, moedaSimbolos?.length])

    useEffect(() => {
        if (paymentCondition?.id) {
            const paymentCond =
                COUNTRY === COUNTRIES.BR ?
                    { id: "100", description: "" }
                    : paymentCondition;

            dispatch(updatePaymentCondition(paymentCond))
        }
    }, [paymentCondition?.id])


    useEffect(() => {
        const scheduledOrder = query.get('scheduled') === 'true';

        if (isDraft) { // rascunho
            const ref = query.get('ref');

            const order = scheduledOrder
                ? scheduled?.items?.find((d) => `${d.draftRef}` === ref)
                : drafts?.items?.find((d) => `${d.draftRef}` === ref);

            dispatch(fetchOrder({
                data: {
                    id: Number(ref),
                    order,
                }
            })).unwrap().then((result) => {
                const bonificationUser = Object.assign(
                    new ApproverResponseApi(),
                    availableApprovalMarketing.find(
                        (w) =>
                            `${w.aprovadorId}` ===
                            `${result?.aprovadorMarketingUsuarioId}`,
                    ),
                );

                dispatch(updateOrder({
                    userBonification: bonificationUser?.toApproverResponse(),
                }))
            }).finally(() => {
                setLoading(false)
            })
        } else { // novo pedido
            dispatch(updateOrder({ customer, orderType }));
            setLoading(false)
        }
    }, []);

    useEffect(() => {
        if (
            selectItemsState?.orderApi?.id
            && isDraft
            && order?.customer?.state
            && custos?.length > 0
            && order.paymentCondition?.id
        ) {
            dispatch(fetchAvailableItems({
                paymentConditionId: order.paymentCondition?.id,
                draft: true,
                tempItems: selectItemsState.orderApi.toOrderRequest().tempItems,
                codigoMoeda: selectItemsState?.orderApi?.codigoMoeda
            }));
        } else if (!isDraft && custos?.length > 0 && order.paymentCondition?.id) {
            dispatch(fetchAvailableItems({
                paymentConditionId: order.paymentCondition?.id,
                codigoMoeda: selectItemsState?.orderApi?.codigoMoeda
            }));
        }
    }, [
        order?.customer?.state,
        custos.length,
        order.paymentCondition?.id
    ])

    return (
        <OrderContextProvider>
            <Page
                title={translate('pages.newOrderBonificatoinMarketing')}
                greyBackground
                onClickGoBack
                menuIcon={<LeftOutlined />}
                loading={loading}
            >
                <UserBonification />
                <SelectItemsHeaderMarketingBonification />
                <SelectItemsBody bonification marketing />
                <SelectItemsActions bonification marketing />
            </Page>
        </OrderContextProvider>
    );
};
